/* WhyGolf app "How it works" — scroll-drawn connector + step reveal.

   Ported from the Replo page's inline script. Both behaviours share one scroll
   pass, which is why this does not use the theme's assets/animations.js: that
   helper only toggles a class on intersect and cannot drive the path draw.

   Changed from the original: scoped to the element rather than document IDs, so
   several instances and theme-editor re-renders both work; listeners are torn
   down on disconnect; the scroll handler is rAF-throttled. */

class AppHowItWorks extends HTMLElement {
  connectedCallback() {
    this.paths = Array.from(this.querySelectorAll('.app-hiw__path--draw'));
    this.track = this.querySelector('.app-hiw__track');
    this.steps = Array.from(this.querySelectorAll('.app-hiw__step'));
    if (!this.track || !this.paths.length) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.ticking = false;

    this.paths.forEach((path) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = length;
      path.style.strokeDashoffset = length;
    });

    if (this.reduced) {
      // CSS already forces dashoffset to 0 and reveals the steps; nothing to drive.
      this.steps.forEach((step) => step.classList.add('is-visible'));
      return;
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.update();

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          });
        },
        { threshold: 0.25 }
      );
      this.steps.forEach((step) => this.observer.observe(step));
    } else {
      this.steps.forEach((step) => step.classList.add('is-visible'));
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.observer) this.observer.disconnect();
  }

  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  };

  update() {
    const rect = this.track.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    // Draw begins when the track's top passes 85vh and completes at 40vh.
    const start = viewportHeight * 0.85;
    const end = viewportHeight * 0.4;
    const total = rect.height + (start - end);
    let progress = (start - rect.top) / total;
    progress = Math.max(0, Math.min(1, progress));

    this.paths.forEach((path) => {
      const length = path.getTotalLength();
      path.style.strokeDashoffset = length * (1 - progress);
    });
  }
}

if (!customElements.get('app-how-it-works')) {
  customElements.define('app-how-it-works', AppHowItWorks);
}
